// src/features/booking/ImageUrlList.tsx
// Ordered list editor for image URLs — ghost input at the bottom commits on
// Enter/blur (no separate "Add" button), each row removable with ×.

import { useState } from 'react';
import { useI18n } from '@react/hooks';

interface ImageUrlListProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  'aria-label'?: string;
}

export function ImageUrlList({ value, onChange, placeholder, 'aria-label': ariaLabel }: ImageUrlListProps) {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');

  const commit = () => {
    const url = draft.trim();
    if (!url) return;
    if (!value.includes(url)) onChange([...value, url]);
    setDraft('');
  };

  const remove = (url: string) => onChange(value.filter((u) => u !== url));

  return (
    <div className="space-y-1.5">
      {value.map((url) => (
        <div key={url} className="flex items-center gap-2 rounded-md border border-border px-2 py-1">
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{url}</span>
          <button
            type="button"
            onClick={() => remove(url)}
            aria-label={t('common.remove')}
            className="shrink-0 text-muted-foreground hover:text-destructive"
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
        onBlur={commit}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="w-full rounded-md border border-dashed border-input bg-transparent px-2 py-1 text-xs text-foreground outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
}
